"use client";

import { forwardRef, useRef, useState } from "react";
import { useOnClickOutside } from "usehooks-ts";

import { trpc } from "@/services/Trpc/client";
import { LoadingSpinner } from "./assets_old/LoadingSpinner";

type SupportFormModalProps = {
  visible: boolean;
  hide: () => void;
};

const SupportFormView = forwardRef<HTMLDivElement, { hide: () => void }>(
  function SupportFormViewInner(props, ref) {
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");
    const [submitted, setSubmitted] = useState(false);

    const mutation = trpc.support.sendSupportRequest.useMutation({
      onSuccess: () => {
        setSubmitted(true);
      },
    });

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (subject.trim() && message.trim().length >= 10) {
        mutation.mutate({ subject, message });
      }
    };

    const handleClose = () => {
      setSubject("");
      setMessage("");
      setSubmitted(false);
      props.hide();
    };

    if (submitted) {
      return (
        <div
          ref={ref}
          className="relative w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
        >
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            className="absolute right-4 top-4 flex h-7 w-7 items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2"
          >
            <span className="pb-0.5 text-xl text-gray-500">&times;</span>
          </button>
          <div className="py-8 text-center">
            <div className="mb-4 text-4xl">&#10003;</div>
            <h2 className="mb-2 text-xl font-semibold text-gray-900">
              Message Sent!
            </h2>
            <p className="text-gray-600">
              We&apos;ll get back to you as soon as possible.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className="relative w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
      >
        <button
          type="button"
          onClick={handleClose}
          aria-label="Close"
          className="absolute right-4 top-4 flex h-7 w-7 items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2"
        >
          <span className="pb-0.5 text-xl text-gray-500">&times;</span>
        </button>

        <h2 className="mb-1 text-xl font-semibold text-gray-900">
          Contact Support
        </h2>
        <p className="mb-4 text-sm text-gray-600">
          We&apos;re here to help! Fill out the form below and we&apos;ll get
          back to you soon.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="subject"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              Subject
            </label>
            <input
              id="subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="What can we help you with?"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
              required
              maxLength={200}
            />
          </div>

          <div>
            <label
              htmlFor="message"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              Message
            </label>
            <textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Please describe your issue or question..."
              rows={5}
              className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
              required
              minLength={10}
              maxLength={5000}
            />
            <p className="mt-1 text-xs text-gray-500">
              {message.length}/5000 characters
            </p>
          </div>

          {mutation.error && (
            <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600">
              {mutation.error.message}
            </div>
          )}

          <button
            type="submit"
            disabled={mutation.isLoading || !subject.trim() || message.trim().length < 10}
            className="flex w-full items-center justify-center rounded-lg bg-violet-600 px-4 py-2.5 font-semibold text-white transition-colors hover:bg-violet-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {mutation.isLoading ? (
              <>
                <LoadingSpinner className="mr-2 h-4 w-4" />
                Sending...
              </>
            ) : (
              "Send Message"
            )}
          </button>
        </form>
      </div>
    );
  }
);

export const SupportFormModal = ({ visible, hide }: SupportFormModalProps) => {
  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref, hide);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <SupportFormView ref={ref} hide={hide} />
    </div>
  );
};
